// Shared label and colour maps for API enums. Values mirror prisma/schema.prisma;
// the API may still return a value we do not know about, so lookups go through
// the *Display helpers, which never throw.

/** True when `value` is one of `values`. Compares by value, so prototype keys such as `constructor` never match. */
export function isOneOf<T extends string>(values: readonly T[], value: unknown): value is T {
  return (values as readonly unknown[]).includes(value);
}

/** Whether `key` is an own key of `record`. `in` would also match inherited keys such as `toString`. */
function isKeyOf<T extends string>(record: Readonly<Record<T, unknown>>, key: string): key is T {
  return Object.hasOwn(record, key);
}

/**
 * Label and colours for an enum value. A value with no label shows its raw text in `fallbackStyle`, so an
 * unknown value from the API is visible rather than dropped.
 */
export function enumDisplay<T extends string>(
  labels: Readonly<Record<T, string>>,
  styles: Readonly<Record<T, BadgeStyle>>,
  value: string,
  fallbackStyle: BadgeStyle,
): EnumDisplay {
  return isKeyOf(labels, value)
    ? { label: labels[value], ...styles[value] }
    : { label: value, ...fallbackStyle };
}

export const TASK_STATUSES = ['pending', 'in_progress', 'in_review', 'completed', 'archived'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

export const TASK_PRIORITIES = ['low', 'medium', 'high', 'urgent'] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];

export const ANNOUNCEMENT_TYPES = ['info', 'warning', 'update', 'maintenance'] as const;
export type AnnouncementType = (typeof ANNOUNCEMENT_TYPES)[number];

export const ANNOUNCEMENT_TYPE_LABEL: Record<AnnouncementType, string> = {
  info: 'Info',
  warning: 'Warning',
  update: 'Update',
  maintenance: 'Maintenance',
};

export function isAnnouncementType(value: string): value is AnnouncementType {
  return isOneOf(ANNOUNCEMENT_TYPES, value);
}

// ComplianceItem.status is a Prisma enum. ComplianceItem.type is free text in the schema;
// these are the values the API documents, and unknown types show their raw value.
export const COMPLIANCE_STATUSES = ['compliant', 'non_compliant', 'pending', 'overdue'] as const;
export type ComplianceStatus = (typeof COMPLIANCE_STATUSES)[number];

export const COMPLIANCE_STATUS_LABEL: Record<ComplianceStatus, string> = {
  compliant: 'Lodged',
  non_compliant: 'Non-compliant',
  pending: 'Due',
  overdue: 'Overdue',
};

export function isComplianceStatus(value: string): value is ComplianceStatus {
  return isOneOf(COMPLIANCE_STATUSES, value);
}

export const COMPLIANCE_TYPES = ['bas', 'payroll', 'super', 'ias', 'fbt', 'annual_return'] as const;
export type ComplianceType = (typeof COMPLIANCE_TYPES)[number];

export const COMPLIANCE_TYPE_LABEL: Record<ComplianceType, string> = {
  bas: 'BAS',
  payroll: 'Payroll',
  super: 'Super',
  ias: 'IAS',
  fbt: 'FBT',
  annual_return: 'Annual return',
};

export function isComplianceType(value: string): value is ComplianceType {
  return isOneOf(COMPLIANCE_TYPES, value);
}

// OnboardingRecord.status is a Prisma String. These are the values the schema documents.
export const ONBOARDING_STATUSES = ['not_started', 'in_progress', 'completed'] as const;
export type OnboardingStatus = (typeof ONBOARDING_STATUSES)[number];

export const ONBOARDING_STATUS_LABEL: Record<OnboardingStatus, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  completed: 'Complete',
};

export const ONBOARDING_STATUS_STYLE: Record<OnboardingStatus, BadgeStyle> = {
  not_started: { color: 'text-gray-700', bg: 'bg-gray-100', dot: 'bg-gray-400' },
  in_progress: { color: 'text-amber-700', bg: 'bg-amber-100', dot: 'bg-amber-500' },
  completed: { color: 'text-emerald-700', bg: 'bg-emerald-100', dot: 'bg-emerald-500' },
};

export function isOnboardingStatus(value: string): value is OnboardingStatus {
  return isOneOf(ONBOARDING_STATUSES, value);
}

/** Label and colours for an onboarding status; unknown values show the raw value in a neutral badge. */
export function onboardingStatusDisplay(value: string): EnumDisplay {
  return enumDisplay(ONBOARDING_STATUS_LABEL, ONBOARDING_STATUS_STYLE, value, NEUTRAL_BADGE);
}

/** Tailwind classes for a badge: text colour, background and indicator dot. */
export interface BadgeStyle {
  color: string;
  bg: string;
  dot: string;
}

export interface EnumDisplay extends BadgeStyle {
  label: string;
}

export const NEUTRAL_BADGE: BadgeStyle = {
  color: 'text-gray-500',
  bg: 'bg-white/8',
  dot: 'bg-gray-400',
};

export const TASK_STATUS_LABEL: Record<TaskStatus, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  in_review: 'In Review',
  completed: 'Completed',
  archived: 'Archived',
};

export const TASK_STATUS_STYLE: Record<TaskStatus, BadgeStyle> = {
  pending: { color: 'text-orange-400', bg: 'bg-orange-500/15', dot: 'bg-orange-400' },
  in_progress: { color: 'text-teal-400', bg: 'bg-teal-500/15', dot: 'bg-teal-400' },
  in_review: { color: 'text-purple-400', bg: 'bg-purple-500/15', dot: 'bg-purple-400' },
  completed: { color: 'text-green-400', bg: 'bg-green-500/15', dot: 'bg-green-400' },
  archived: { color: 'text-gray-500', bg: 'bg-white/8', dot: 'bg-gray-400' },
};

export const TASK_PRIORITY_LABEL: Record<TaskPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
};

export const TASK_PRIORITY_STYLE: Record<TaskPriority, BadgeStyle> = {
  low: { color: 'text-gray-500', bg: 'bg-white/8', dot: 'bg-gray-400' },
  medium: { color: 'text-orange-400', bg: 'bg-orange-500/15', dot: 'bg-orange-400' },
  high: { color: 'text-red-400', bg: 'bg-red-500/15', dot: 'bg-red-400' },
  urgent: { color: 'text-rose-300', bg: 'bg-rose-500/25', dot: 'bg-rose-400' },
};

export function isTaskStatus(value: string): value is TaskStatus {
  return isOneOf(TASK_STATUSES, value);
}

export function isTaskPriority(value: string): value is TaskPriority {
  return isOneOf(TASK_PRIORITIES, value);
}

/** Label and colours for a task status; unknown values show the raw value in a neutral badge. */
export function taskStatusDisplay(value: string): EnumDisplay {
  return enumDisplay(TASK_STATUS_LABEL, TASK_STATUS_STYLE, value, NEUTRAL_BADGE);
}

/** Label and colours for a task priority; unknown values show the raw value in a neutral badge. */
export function taskPriorityDisplay(value: string): EnumDisplay {
  return enumDisplay(TASK_PRIORITY_LABEL, TASK_PRIORITY_STYLE, value, NEUTRAL_BADGE);
}

// Connection.status is a Prisma String. The schema documents these values (uppercase, unlike the other enums here).
export const CONNECTION_STATUSES = ['CONNECTED', 'DISCONNECTED', 'ERROR', 'PENDING'] as const;
export type ConnectionStatus = (typeof CONNECTION_STATUSES)[number];

export const CONNECTION_STATUS_LABEL: Record<ConnectionStatus, string> = {
  CONNECTED: 'Connected',
  DISCONNECTED: 'Disconnected',
  ERROR: 'Error',
  PENDING: 'Pending',
};

export const CONNECTION_STATUS_STYLE: Record<ConnectionStatus, BadgeStyle> = {
  CONNECTED: { color: 'text-green-500', bg: 'bg-green-500/15', dot: 'bg-green-400' },
  DISCONNECTED: { color: 'text-gray-500', bg: 'bg-white/8', dot: 'bg-gray-400' },
  ERROR: { color: 'text-red-400', bg: 'bg-red-500/15', dot: 'bg-red-400' },
  PENDING: { color: 'text-amber-400', bg: 'bg-amber-500/15', dot: 'bg-amber-400' },
};

export function isConnectionStatus(value: string): value is ConnectionStatus {
  return isOneOf(CONNECTION_STATUSES, value);
}

/** Label and colours for a connection status; unknown values show the raw value in a neutral badge. */
export function connectionStatusDisplay(value: string): EnumDisplay {
  return enumDisplay(CONNECTION_STATUS_LABEL, CONNECTION_STATUS_STYLE, value, NEUTRAL_BADGE);
}

// XeroConnection.status is a Prisma String (default `active`). The schema documents these values.
export const XERO_CONNECTION_STATUSES = ['active', 'expired', 'revoked'] as const;
export type XeroConnectionStatus = (typeof XERO_CONNECTION_STATUSES)[number];

export const XERO_CONNECTION_STATUS_LABEL: Record<XeroConnectionStatus, string> = {
  active: 'Active',
  expired: 'Expired',
  revoked: 'Revoked',
};

export function isXeroConnectionStatus(value: string): value is XeroConnectionStatus {
  return isOneOf(XERO_CONNECTION_STATUSES, value);
}

/** Label for a Xero connection status; an unknown value shows its raw text. */
export function xeroConnectionStatusLabel(value: string): string {
  return isXeroConnectionStatus(value) ? XERO_CONNECTION_STATUS_LABEL[value] : value;
}

// User.status and UserFirmRole.role are Prisma Strings, not enums. These are the values the schema documents.
export const USER_STATUSES = ['active', 'suspended', 'deactivated'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

export const USER_STATUS_LABEL: Record<UserStatus, string> = {
  active: 'Active',
  suspended: 'Suspended',
  deactivated: 'Deactivated',
};

export const USER_STATUS_STYLE: Record<UserStatus, BadgeStyle> = {
  active: { color: 'text-green-500', bg: 'bg-green-500/15', dot: 'bg-green-400' },
  suspended: { color: 'text-amber-400', bg: 'bg-amber-500/15', dot: 'bg-amber-400' },
  deactivated: { color: 'text-gray-500', bg: 'bg-white/8', dot: 'bg-gray-400' },
};

export function isUserStatus(value: string): value is UserStatus {
  return isOneOf(USER_STATUSES, value);
}

/** Label and colours for a user status; unknown values show the raw value in a neutral badge. */
export function userStatusDisplay(value: string): EnumDisplay {
  return enumDisplay(USER_STATUS_LABEL, USER_STATUS_STYLE, value, NEUTRAL_BADGE);
}

export const USER_ROLES = ['owner', 'admin', 'senior_accountant', 'accountant', 'viewer'] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const USER_ROLE_LABEL: Record<UserRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  senior_accountant: 'Senior accountant',
  accountant: 'Accountant',
  viewer: 'Viewer',
};

/** Solid badge background per role. Role badges use white text, unlike the tinted status badges. */
export const USER_ROLE_BADGE_COLOR: Record<UserRole, string> = {
  owner: 'bg-purple-500',
  admin: 'bg-red-500',
  senior_accountant: 'bg-blue-500',
  accountant: 'bg-teal-500',
  viewer: 'bg-gray-500',
};

const UNKNOWN_ROLE_BADGE_COLOR = 'bg-gray-500';

export function isUserRole(value: string): value is UserRole {
  return isOneOf(USER_ROLES, value);
}

/** A role's API value, label and solid badge colour. */
export interface UserRoleDisplay {
  value: string;
  label: string;
  badgeColor: string;
}

/** Label and colour for a role; unknown roles show the raw value in a neutral colour. */
export function userRoleDisplay(value: string): UserRoleDisplay {
  return isUserRole(value)
    ? { value, label: USER_ROLE_LABEL[value], badgeColor: USER_ROLE_BADGE_COLOR[value] }
    : { value, label: value, badgeColor: UNKNOWN_ROLE_BADGE_COLOR };
}

// RecurringTemplate.frequency is a Prisma String. The schema documents these values.
export const RECURRING_FREQUENCIES = ['daily', 'weekly', 'fortnightly', 'monthly', 'quarterly', 'annually'] as const;
export type RecurringFrequency = (typeof RECURRING_FREQUENCIES)[number];

export const RECURRING_FREQUENCY_LABEL: Record<RecurringFrequency, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  fortnightly: 'Fortnightly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annually: 'Annually',
};

export function isRecurringFrequency(value: string): value is RecurringFrequency {
  return isOneOf(RECURRING_FREQUENCIES, value);
}

// Document.type is a Prisma String. The schema documents these values.
export const DOCUMENT_TYPES = ['receipt', 'invoice', 'statement', 'timesheet'] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number];

export const DOCUMENT_TYPE_LABEL: Record<DocumentType, string> = {
  receipt: 'Receipt',
  invoice: 'Invoice',
  statement: 'Statement',
  timesheet: 'Timesheet',
};

export function isDocumentType(value: string): value is DocumentType {
  return isOneOf(DOCUMENT_TYPES, value);
}

// MessageThread.channel and InboundMessage/OutboundMessage.channel are Prisma Strings. The schema documents these values.
export const COMMUNICATION_CHANNELS = ['email', 'whatsapp', 'sms'] as const;
export type CommunicationChannel = (typeof COMMUNICATION_CHANNELS)[number];

export const COMMUNICATION_CHANNEL_LABEL: Record<CommunicationChannel, string> = {
  email: 'Email',
  whatsapp: 'WhatsApp',
  sms: 'SMS',
};

export function isCommunicationChannel(value: string): value is CommunicationChannel {
  return isOneOf(COMMUNICATION_CHANNELS, value);
}

// MessageThread.status is a Prisma String. The schema documents these values.
export const THREAD_STATUSES = ['open', 'resolved', 'closed'] as const;
export type ThreadStatus = (typeof THREAD_STATUSES)[number];

export const THREAD_STATUS_LABEL: Record<ThreadStatus, string> = {
  open: 'Open',
  resolved: 'Resolved',
  closed: 'Closed',
};

export const THREAD_STATUS_STYLE: Record<ThreadStatus, BadgeStyle> = {
  open: { color: 'text-teal-700', bg: 'bg-teal-100', dot: 'bg-teal-500' },
  resolved: { color: 'text-green-700', bg: 'bg-green-100', dot: 'bg-green-500' },
  closed: { color: 'text-gray-700', bg: 'bg-gray-100', dot: 'bg-gray-400' },
};

export function isThreadStatus(value: string): value is ThreadStatus {
  return isOneOf(THREAD_STATUSES, value);
}

/** Label and colours for a thread status; unknown values show the raw value in a neutral badge. */
export function threadStatusDisplay(value: string): EnumDisplay {
  return enumDisplay(THREAD_STATUS_LABEL, THREAD_STATUS_STYLE, value, NEUTRAL_BADGE);
}

// MessageDirection is a Prisma enum.
export const MESSAGE_DIRECTIONS = ['inbound', 'outbound'] as const;
export type MessageDirection = (typeof MESSAGE_DIRECTIONS)[number];

export const MESSAGE_DIRECTION_LABEL: Record<MessageDirection, string> = {
  inbound: 'Inbound',
  outbound: 'Outbound',
};

// OutboundMessage.status is a Prisma String. The schema documents these values.
export const OUTBOUND_MESSAGE_STATUSES = ['draft', 'pending', 'sent', 'delivered', 'failed'] as const;
export type OutboundMessageStatus = (typeof OUTBOUND_MESSAGE_STATUSES)[number];

export const OUTBOUND_MESSAGE_STATUS_LABEL: Record<OutboundMessageStatus, string> = {
  draft: 'Draft',
  pending: 'Pending',
  sent: 'Sent',
  delivered: 'Delivered',
  failed: 'Failed',
};

export const OUTBOUND_MESSAGE_STATUS_STYLE: Record<OutboundMessageStatus, BadgeStyle> = {
  draft: { color: 'text-gray-700', bg: 'bg-gray-100', dot: 'bg-gray-400' },
  pending: { color: 'text-amber-700', bg: 'bg-amber-100', dot: 'bg-amber-500' },
  sent: { color: 'text-blue-700', bg: 'bg-blue-100', dot: 'bg-blue-500' },
  delivered: { color: 'text-green-700', bg: 'bg-green-100', dot: 'bg-green-500' },
  failed: { color: 'text-red-700', bg: 'bg-red-100', dot: 'bg-red-500' },
};

/** Label and colours for an outbound message status; unknown values show the raw value in a neutral badge. */
export function outboundMessageStatusDisplay(value: string): EnumDisplay {
  return enumDisplay(OUTBOUND_MESSAGE_STATUS_LABEL, OUTBOUND_MESSAGE_STATUS_STYLE, value, NEUTRAL_BADGE);
}

// Client.status is a Prisma String. The API's write schema accepts active, inactive and prospect; the Prisma comment
// also lists suspended. Unknown values show the raw value.
export const CLIENT_STATUSES = ['active', 'inactive', 'prospect', 'suspended'] as const;
export type ClientStatus = (typeof CLIENT_STATUSES)[number];

export const CLIENT_STATUS_LABEL: Record<ClientStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
  prospect: 'Prospect',
  suspended: 'Suspended',
};

export const CLIENT_STATUS_STYLE: Record<ClientStatus, BadgeStyle> = {
  active: { color: 'text-emerald-700', bg: 'bg-emerald-50', dot: 'bg-emerald-500' },
  inactive: { color: 'text-gray-700', bg: 'bg-gray-100', dot: 'bg-gray-400' },
  prospect: { color: 'text-blue-700', bg: 'bg-blue-50', dot: 'bg-blue-500' },
  suspended: { color: 'text-amber-700', bg: 'bg-amber-50', dot: 'bg-amber-500' },
};

export function isClientStatus(value: string): value is ClientStatus {
  return isOneOf(CLIENT_STATUSES, value);
}

/** Label and colours for a client status; unknown values show the raw value in a neutral badge. */
export function clientStatusDisplay(value: string): EnumDisplay {
  return enumDisplay(CLIENT_STATUS_LABEL, CLIENT_STATUS_STYLE, value, NEUTRAL_BADGE);
}

// Client.healthStatus is the Prisma enum ClientHealthStatus.
export const CLIENT_HEALTH_STATUSES = ['healthy', 'at_risk', 'critical'] as const;
export type ClientHealthStatus = (typeof CLIENT_HEALTH_STATUSES)[number];

export const CLIENT_HEALTH_LABEL: Record<ClientHealthStatus, string> = {
  healthy: 'Healthy',
  at_risk: 'At risk',
  critical: 'Critical',
};

export const CLIENT_HEALTH_STYLE: Record<ClientHealthStatus, BadgeStyle> = {
  healthy: { color: 'text-emerald-700', bg: 'bg-emerald-50', dot: 'bg-emerald-500' },
  at_risk: { color: 'text-amber-700', bg: 'bg-amber-50', dot: 'bg-amber-500' },
  critical: { color: 'text-red-700', bg: 'bg-red-50', dot: 'bg-red-500' },
};

export function isClientHealthStatus(value: string): value is ClientHealthStatus {
  return isOneOf(CLIENT_HEALTH_STATUSES, value);
}

/** Label and colours for a client health status; unknown values show the raw value in a neutral badge. */
export function clientHealthDisplay(value: string): EnumDisplay {
  return enumDisplay(CLIENT_HEALTH_LABEL, CLIENT_HEALTH_STYLE, value, NEUTRAL_BADGE);
}

// WorkflowTemplate.category is a Prisma String. The schema documents these values.
export const WORKFLOW_CATEGORIES = ['bas_preparation', 'payroll', 'onboarding', 'reconciliation'] as const;
export type WorkflowCategory = (typeof WORKFLOW_CATEGORIES)[number];

export const WORKFLOW_CATEGORY_LABEL: Record<WorkflowCategory, string> = {
  bas_preparation: 'BAS preparation',
  payroll: 'Payroll',
  onboarding: 'Onboarding',
  reconciliation: 'Reconciliation',
};

export function isWorkflowCategory(value: string): value is WorkflowCategory {
  return isOneOf(WORKFLOW_CATEGORIES, value);
}

/** Label for a workflow category; an unknown value shows its raw text. */
export function workflowCategoryLabel(value: string): string {
  return isWorkflowCategory(value) ? WORKFLOW_CATEGORY_LABEL[value] : value;
}
